import type {
  AdminOverview,
  AdminStats,
  ApiError,
  ApplicantView,
  AppSettingView,
  DecisionAction,
  DecisionResponse,
  JournalRow,
  AdminUserRow,
  ModeratorRow,
  Page,
  QueueRow,
  ReportDetail,
  ReportReason,
  SendCodeResponse,
  TokenPair,
  UserRole,
} from './types'

/**
 * API client.
 *
 * Two things it deliberately does:
 *
 * 1. Keeps the refresh token out of `localStorage`. Only the short-lived access
 *    token is held in memory; the refresh token lives in `sessionStorage`, so a
 *    closed tab ends the session and an XSS payload cannot lift a 60-day
 *    credential out of persistent storage.
 * 2. Refreshes once, transparently, on a 401, and de-duplicates concurrent
 *    refreshes — several panels load at once and would otherwise each burn a
 *    single-use refresh token.
 */

const REFRESH_KEY = 'sebeb.admin.refresh'

/** Thrown for every non-2xx response; carries the server's Azerbaijani copy. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ApiError,
  ) {
    super(payload.message)
    this.name = 'ApiRequestError'
  }

  get code(): string {
    return this.payload.code
  }

  /** Field-level problems, for forms that show errors inline. */
  get details(): Record<string, string> {
    return this.payload.details ?? {}
  }
}

let accessToken: string | null = null
let refreshInFlight: Promise<TokenPair | null> | null = null

/** Notified when the session ends, so the UI can return to the login screen. */
type SessionListener = () => void
const sessionListeners = new Set<SessionListener>()

export function onSessionEnded(listener: SessionListener): () => void {
  sessionListeners.add(listener)
  return () => sessionListeners.delete(listener)
}

export function setSession(tokens: TokenPair): void {
  accessToken = tokens.accessToken
  sessionStorage.setItem(REFRESH_KEY, tokens.refreshToken)
}

export function clearSession(): void {
  accessToken = null
  sessionStorage.removeItem(REFRESH_KEY)
}

export function hasStoredSession(): boolean {
  return sessionStorage.getItem(REFRESH_KEY) !== null
}

function endSession(): void {
  clearSession()
  sessionListeners.forEach((listener) => listener())
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiError
    if (typeof body?.code === 'string' && typeof body?.message === 'string') return body
  } catch {
    // Fall through — a proxy or gateway error will not be our envelope.
  }
  return {
    code: 'network_error',
    message: 'Serverlə əlaqə qurulmadı. Bir az sonra yenidən yoxlayın.',
  }
}

async function performRefresh(): Promise<TokenPair | null> {
  const refreshToken = sessionStorage.getItem(REFRESH_KEY)
  if (!refreshToken) return null

  try {
    const response = await fetch('/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) {
      // The server rejected the token itself, so it is spent — drop it.
      clearSession()
      return null
    }

    const tokens = (await response.json()) as TokenPair
    setSession(tokens)
    return tokens
  } catch {
    // A network failure says nothing about the token's validity. Keeping it
    // lets the next attempt succeed once connectivity returns.
    return null
  }
}

/**
 * The single refresh path.
 *
 * Every caller shares one in-flight attempt. Refresh tokens are single-use and
 * rotate on the server, so two overlapping refreshes would spend the same token
 * twice and the loser would log the user out — which is exactly what React's
 * StrictMode double-invoked restore effect used to do in development.
 */
function sharedRefresh(): Promise<TokenPair | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, string | number | undefined>
  /** Set for the login calls, which run before a session exists. */
  anonymous?: boolean
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, anonymous = false } = options

  const url = new URL(path, window.location.origin)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value))
    }
  }

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {}
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (!anonymous && accessToken) headers.Authorization = `Bearer ${accessToken}`

    return fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  }

  let response: Response
  try {
    response = await send()
  } catch {
    throw new ApiRequestError(0, {
      code: 'network_error',
      message: 'Serverlə əlaqə qurulmadı. İnternet bağlantını yoxla.',
    })
  }

  // One transparent retry after a refresh; a second 401 means the session is
  // genuinely over.
  if (response.status === 401 && !anonymous) {
    if ((await sharedRefresh()) !== null) {
      response = await send()
    }
    if (response.status === 401) {
      endSession()
      throw new ApiRequestError(401, await parseError(response))
    }
  }

  if (!response.ok) {
    throw new ApiRequestError(response.status, await parseError(response))
  }

  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** The endpoints the panel uses, grouped as the screens are. */
export const api = {
  auth: {
    /** Screen 9a — the panel signs in with the same six-digit code flow. */
    sendCode: (email: string) =>
      request<SendCodeResponse>('/v1/auth/email/code', {
        method: 'POST',
        anonymous: true,
        body: { email, purpose: 'LOGIN' satisfies string },
      }),

    verifyCode: (email: string, code: string) =>
      request<TokenPair>('/v1/auth/email/verify', {
        method: 'POST',
        anonymous: true,
        body: { email, code, purpose: 'LOGIN', deviceLabel: 'Moderasiya paneli' },
      }),

    logout: () => request<void>('/v1/auth/logout', { method: 'POST' }),

    /**
     * Restores a session on reload from the stored refresh token.
     *
     * Goes through the shared refresh path rather than issuing its own request,
     * so a double-invoked effect cannot spend the single-use token twice.
     */
    restore: (): Promise<TokenPair | null> => sharedRefresh(),
  },

  /** Screen 7a. */
  overview: () => request<AdminOverview>('/v1/admin/overview'),

  /** Screens 7b / 8c. */
  queue: (params: {
    bucket?: 'PENDING' | 'MINE' | 'RESOLVED' | 'APPEALED'
    reason?: ReportReason | 'all'
    limit?: number
    offset?: number
  }) =>
    request<Page<QueueRow>>('/v1/mod/queue', {
      query: {
        bucket: params.bucket,
        reason: params.reason === 'all' ? undefined : params.reason,
        limit: params.limit,
        offset: params.offset,
      },
    }),

  claimQueue: (count = 5) =>
    request<{ claimed: number[] }>('/v1/mod/queue/claim', {
      method: 'POST',
      query: { count },
    }),

  /** Screen 7c. */
  report: (id: number) => request<ReportDetail>(`/v1/mod/reports/${id}`),

  decide: (id: number, action: DecisionAction, note?: string, blockDays?: number) =>
    request<DecisionResponse>(`/v1/mod/reports/${id}/decision`, {
      method: 'POST',
      body: { action, note, blockDays },
    }),

  reviewAppeal: (id: number, uphold: boolean, note?: string) =>
    request<void>(`/v1/mod/reports/${id}/appeal/review`, {
      method: 'POST',
      body: { uphold, note },
    }),

  /** Admin · İstifadəçilər. */
  users: (params: { q?: string; limit?: number; offset?: number }) =>
    request<Page<AdminUserRow>>('/v1/admin/users', { query: params }),

  /** Screen 7d. */
  moderators: () => request<ModeratorRow[]>('/v1/admin/moderators'),

  updateModerator: (
    id: string,
    change: { role?: UserRole; suspend?: boolean; suspensionReason?: string; remove?: boolean },
  ) => request<ModeratorRow>(`/v1/admin/moderators/${id}`, { method: 'PATCH', body: change }),

  /** Screen 7e. */
  journal: (params: { days?: number; actorId?: string; limit?: number; offset?: number }) =>
    request<Page<JournalRow>>('/v1/admin/journal', { query: params }),

  journalCsvUrl: (days?: number) =>
    `/v1/admin/journal/export.csv${days ? `?days=${days}` : ''}`,

  /** Screen 7f. */
  applications: () => request<ApplicantView[]>('/v1/admin/applications'),

  decideApplication: (id: string, accept: boolean, note?: string, role?: UserRole) =>
    request<void>(`/v1/admin/applications/${id}/decide`, {
      method: 'POST',
      body: { accept, note, role },
    }),

  /** Screen 8h. */
  stats: (from?: string, to?: string) =>
    request<AdminStats>('/v1/admin/stats', { query: { from, to } }),

  settings: () => request<AppSettingView[]>('/v1/admin/settings'),

  updateSettings: (values: Record<string, string>) =>
    request<AppSettingView[]>('/v1/admin/settings', { method: 'PATCH', body: { values } }),
}

/**
 * Downloads the journal CSV.
 *
 * A plain link cannot carry the Authorization header, so the file is fetched
 * and handed to the browser as a blob.
 */
export async function downloadJournalCsv(days?: number): Promise<void> {
  const response = await fetch(api.journalCsvUrl(days), {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  })
  if (!response.ok) throw new ApiRequestError(response.status, await parseError(response))

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'moderasiya-jurnali.csv'
  link.click()
  URL.revokeObjectURL(url)
}
